import { createFileRoute, Link, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { Fragment } from "react";
import { AppSidebar } from "~frontend/components/app-sidebar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator
} from "~frontend/components/ui/breadcrumb";
import { Separator } from "~frontend/components/ui/separator";
import {
    SidebarInset,
    SidebarProvider,
    SidebarTrigger,
} from "~frontend/components/ui/sidebar";
import { auth } from "~frontend/lib/auth";

export const Route = createFileRoute("/_LoggedIn")({
    ssr: false,
    beforeLoad: async () => {
        const { data: session } = await auth.getSession();
        if (!session) {
            throw redirect({
                to: "/login",
            });
        }
    },
    component: DashboardLayout,
});

function DashboardLayout() {
    const { data: session } = auth.useSession();
    const pathname = useRouterState({ select: (s) => s.location.pathname });

    if (!session) {
        return null; // Will redirect due to beforeLoad
    }

    const segments = pathname.split("/").filter(Boolean);
    const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);
    const toTitleCase = (value: string) => value.split("-").map(capitalize).join(" ");

    return (
        <SidebarProvider>
            <AppSidebar />
            <SidebarInset>
                <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
                    <div className="flex items-center gap-2 px-4">
                        <SidebarTrigger className="-ml-1" />
                        <Separator
                            orientation="vertical"
                            className="mr-2 data-[orientation=vertical]:h-4"
                        />
                        {segments.length > 0 ? (
                            <Breadcrumb>
                                <BreadcrumbList>
                                    {segments.map((segment, index) => {
                                        const isLast = index === segments.length - 1;
                                        const label = toTitleCase(decodeURIComponent(segment));
                                        const targetPath = "/" + segments.slice(0, index + 1).join("/");
                                        return (
                                            <Fragment key={targetPath}>
                                                <BreadcrumbItem>
                                                    {isLast ? (
                                                        <BreadcrumbPage>{label}</BreadcrumbPage>
                                                    ) : (
                                                        <BreadcrumbLink asChild>
                                                            <Link to={targetPath}>{label}</Link>
                                                        </BreadcrumbLink>
                                                    )}
                                                </BreadcrumbItem>
                                                {!isLast ? <BreadcrumbSeparator /> : null}
                                            </Fragment>
                                        );
                                    })}
                                </BreadcrumbList>
                            </Breadcrumb>
                        ) : null}
                    </div>
                </header>
                <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
                    <Outlet />
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
} 